import { Injectable } from '@angular/core';
import { GetOpenEnrollmentRule } from '../../family-form/types/qualifying-life-event.response.interface';
import { SEP_CIRCUMSTANCE_ID } from 'src/app/angular-elements/shared/interfaces/special-enrollment-circumstances.interface';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { DateService } from 'src/app/shared/services/date.service';
import { SpecialEnrollmentCircumstanceStateStructure } from '../../plan-enrollment/individual-plan-enrollment-form/configs/interfaces/special-enrollment-circumstance.StateStructure.interface';
import moment, { Moment } from 'moment';
import { environment } from 'src/environments/environment';
import { SepEffectiveDateConfig } from '../../family-form/types/qualifying-life-event.response.interface';
export const HMLifeEvents = 
new Set(
  [ SEP_CIRCUMSTANCE_ID.ICHRA, 
    SEP_CIRCUMSTANCE_ID.QSEHRA, 
    SEP_CIRCUMSTANCE_ID.NEW_ENROLLEE, 
    SEP_CIRCUMSTANCE_ID.COURT_ORDER, 
    SEP_CIRCUMSTANCE_ID.DV_OR_ABANDONMENT, 
    SEP_CIRCUMSTANCE_ID.MEDICAID_OR_CHIP_DENIAL, 
    SEP_CIRCUMSTANCE_ID.LOSS_OF_COVERAGE
  ]
)
export const MNLifeEvents = 
new Set(
  [ SEP_CIRCUMSTANCE_ID.ICHRA, 
    SEP_CIRCUMSTANCE_ID.QSEHRA, 
    SEP_CIRCUMSTANCE_ID.BIRTH, 
    SEP_CIRCUMSTANCE_ID.ADOPTION, 
    SEP_CIRCUMSTANCE_ID.MARRIAGE, 
    SEP_CIRCUMSTANCE_ID.DEATH_POLICYHOLDER, 
    SEP_CIRCUMSTANCE_ID.LOSS_OF_MEDICAID, 
    SEP_CIRCUMSTANCE_ID.LEGAL_SEPARATION, 
    SEP_CIRCUMSTANCE_ID.FOSTER_CARE, 
    SEP_CIRCUMSTANCE_ID.COURT_ORDER_MN, 
    SEP_CIRCUMSTANCE_ID.PREGNANCY, 
    SEP_CIRCUMSTANCE_ID.LOSS_OF_COVERAGE_EMPLOYER, 
    SEP_CIRCUMSTANCE_ID.LOSS_OF_COVERAGE_BENEFIT, 
    SEP_CIRCUMSTANCE_ID.MEDICARE_ENTITLED, 
    SEP_CIRCUMSTANCE_ID.LOSS_CHILD_DEPENDENT, 
    SEP_CIRCUMSTANCE_ID.LOSS_OF_COBRA, 
    SEP_CIRCUMSTANCE_ID.MOVING_OUT_HMO, 
    SEP_CIRCUMSTANCE_ID.RESIDENCE_CHANGE, 
    SEP_CIRCUMSTANCE_ID.RELEASE_INCARCERATION, 
    SEP_CIRCUMSTANCE_ID.MILITARY_SERVICE, 
    SEP_CIRCUMSTANCE_ID.FINANCIAL_CHANGE
  ]);

export const WYLifeEvents = 
new Set(
  [ SEP_CIRCUMSTANCE_ID.BIRTH, 
    SEP_CIRCUMSTANCE_ID.ADOPTION, 
    SEP_CIRCUMSTANCE_ID.MARRIAGE, 
    SEP_CIRCUMSTANCE_ID.DEATH_POLICYHOLDER, 
    SEP_CIRCUMSTANCE_ID.LOSS_OF_MEDICAID, 
    SEP_CIRCUMSTANCE_ID.LEGAL_SEPARATION, 
    SEP_CIRCUMSTANCE_ID.LOSS_OF_COVERAGE, 
    SEP_CIRCUMSTANCE_ID.LEGAL_GUARDIANSHIP, 
    SEP_CIRCUMSTANCE_ID.OTHER
  ]);

@Injectable({
  providedIn: 'root'
})
export class SpecialEnrollmentLifeEventDrivenEffectiveDateService {

  constructor(
    private date: DateService,
    private reducerUtilityService: ReducerUtilityService
  ) { }

  /** Special Enrollment Events have a generated effective date, based on the life event date */

  getSepEffectiveDates(lifeEvent: GetOpenEnrollmentRule, serviceEffectiveDates):Moment[]{
    const payloadStates = this.reducerUtilityService.getPayloadStates();
    const fetchFromConfig = this.reducerUtilityService.getComponentStates().SepLifeEventForm.fetchFromConfig;
    const specialEnrollmentState: SpecialEnrollmentCircumstanceStateStructure = payloadStates['special-enrollment-circumstance'];
    let effectiveDatesAsMoment: moment.Moment[] = [];

    const normalize = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const firstOfNextMonthAfterEventDate: string[] = ["FIRST_OF_NEXT_MONTH_AFTER_APP_DATE"];

    if (specialEnrollmentState?.dateOfLifeEvent && specialEnrollmentState?.lifeEvent) {
      const dateOfLifeEvent: moment.Moment = this.date.getDateAsMoment(fetchFromConfig ? specialEnrollmentState.dateOfLifeEvent : this.reducerUtilityService.getComponentStates().SepLifeEventForm.lifeEventDateValue);
      const currentDate: moment.Moment = this.date.getDateAsMoment(fetchFromConfig ? specialEnrollmentState.lifeEvent.asOfDate : lifeEvent.asOfDate); // Devon - 2024-08-26 - during saved quote experiences, this will need to be calculated
      const effectiveDateRuleInd = parseInt(fetchFromConfig ? specialEnrollmentState.lifeEvent.effectiveDateRuleId : lifeEvent.effectiveDateRuleId, 10); // Values come from either the /openEnrollmentRules services or are manually, generated in the forms-as-json to satisfy interface ( they are not used )    
      const isFirstOfTheMonth = !!(dateOfLifeEvent.date() === 1);
      const isLifeEventDateAFutureOccurrence = currentDate < dateOfLifeEvent;
      const isLifeEventDateSameAsCurrentDate = dateOfLifeEvent.isSame(currentDate, 'day');
      
      const dateScenarios = {
        eventRelative: {
          nextMonth: {
            firstDay: dateOfLifeEvent.clone().month(dateOfLifeEvent.month() + 1).date(1)
          },
          secondMonth: {
            firstDay: dateOfLifeEvent.clone().month(dateOfLifeEvent.month() + 2).date(1)
          },
          firstDay: dateOfLifeEvent.clone().date(1),
          dateOf: dateOfLifeEvent.clone()
        },
        todayRelative: {
          nextMonth: {
            firstDay: currentDate.clone().month(currentDate.month() + 1).date(1)
          },
          secondMonth: {
            firstDay: currentDate.clone().month(currentDate.month() + 2).date(1)
          }
        }
        
      }

      const config: SepEffectiveDateConfig = lifeEvent.effectiveDateRuleConfig;

      if (config) {
        const ruleKeys = this.resolveRuleKeys(
          lifeEvent.lifeEventId,
          specialEnrollmentState.lifeEvent.effectiveDateRuleId,
          isLifeEventDateAFutureOccurrence,
          isLifeEventDateSameAsCurrentDate,
          isFirstOfTheMonth,
          config,
          firstOfNextMonthAfterEventDate
        );

        if (ruleKeys === null) {
          // REJECT: no effective dates available for this life event / timing
          console.warn(`getSepEffectiveDates: REJECT rule applied for lifeEvent=${lifeEvent.lifeEventId}`);
          return [];
        }

        return this.resolveRuleKeysToDates(ruleKeys, dateScenarios, serviceEffectiveDates);
      }


      let shouldAddEventDate = this.shouldAddEventDate(lifeEvent.lifeEventId);
  
      switch(effectiveDateRuleInd){
        case(1):
          //push next two months first day relative to today's date (service dates)
          for(const effDate of serviceEffectiveDates){
            effectiveDatesAsMoment.push(effDate);
          }
          break;
        case(2):
          //only push next month first relative to today's date
          effectiveDatesAsMoment.push(this.date.getDateAsMoment(dateScenarios.todayRelative.nextMonth.firstDay));
          break;
        case(3):
          //based off future or past occurrence of date with the inclusion of the first of the month exception
          //can only use date of life event as an eff date if it a first of the month and not a past date
          if (isLifeEventDateAFutureOccurrence && !isFirstOfTheMonth) {
            effectiveDatesAsMoment.push(this.date.getDateAsMoment(dateScenarios.eventRelative.nextMonth.firstDay));
            break;
          } else if((isLifeEventDateAFutureOccurrence || isLifeEventDateSameAsCurrentDate) && isFirstOfTheMonth) {
            shouldAddEventDate = true;
            break;
          } else {
            effectiveDatesAsMoment.push(this.date.getDateAsMoment(dateScenarios.todayRelative.nextMonth.firstDay));
            break;
          }
        case(4):
          //based off future or past occurrence of date
          if (isLifeEventDateAFutureOccurrence) {
            effectiveDatesAsMoment.push(this.date.getDateAsMoment(dateScenarios.eventRelative.nextMonth.firstDay));
            break;
          } else {
            effectiveDatesAsMoment.push(this.date.getDateAsMoment(dateScenarios.todayRelative.nextMonth.firstDay));
            break;
          }
        case(5):
          /** Nik: WY specific scenario where we add next month first day & service dates
           * I believe it was originally a coding error that now exists in perpetuity
           * this scenario will most likely go away with the WY effective date updates */
          effectiveDatesAsMoment.push(this.date.getDateAsMoment(dateScenarios.eventRelative.nextMonth.firstDay));
          for(const effDate of serviceEffectiveDates){
            effectiveDatesAsMoment.push(effDate);
          }
          break;
        case (6):
          //add no other effective dates besides life event date
          //having this as a specific rule allows us to have the default as getting service dates
          //also allows us clarity for devs
          effectiveDatesAsMoment.push(dateOfLifeEvent);
          break;
        default: 
          for(const effDate of serviceEffectiveDates){
            effectiveDatesAsMoment.push(effDate);
          }
          break;
      }
      
      if(shouldAddEventDate){
        effectiveDatesAsMoment.push(dateOfLifeEvent);
      }
  
      return effectiveDatesAsMoment;
      //TODO - Nik - REMOVE: MN Specific Logic, needs kept through July 17th 2026 release
      } else if(payloadStates['special-enrollment-interstitial']){
        const currentDate = moment();
        const todayRelativeNextMonthFirstDay = currentDate.clone().month(currentDate.month() + 1).date(1);
        
        if(payloadStates['special-enrollment-interstitial'].isApplicableCircumstance === "Yes" && payloadStates['special-enrollment-child']?.hasAcquiredChild === "Yes" && payloadStates['special-enrollment-child-date']){
          effectiveDatesAsMoment.push(moment(payloadStates['special-enrollment-child-date'].childAcquisitionDate));
          effectiveDatesAsMoment.push(todayRelativeNextMonthFirstDay);
        }else{
          for(const effDate of serviceEffectiveDates){
            effectiveDatesAsMoment.push(effDate);
          }
        }
        return effectiveDatesAsMoment;

      }
      console.error("Date of life event or life event object is not defined!");
      return null;
    }

    shouldAddEventDate(lifeEventId: string){
      switch(lifeEventId){
        case(SEP_CIRCUMSTANCE_ID.BIRTH):
          return true;
        case(SEP_CIRCUMSTANCE_ID.ADOPTION):
          return true;
        case(SEP_CIRCUMSTANCE_ID.LEGAL_GUARDIANSHIP):
          return true;
        case(SEP_CIRCUMSTANCE_ID.FOSTER_CARE):
          return true;
        case(SEP_CIRCUMSTANCE_ID.COURT_ORDER_MN):
          return true;
        case(SEP_CIRCUMSTANCE_ID.DEATH_POLICYHOLDER):
          if(environment.initData.brand.ASPID === "WY"){
            return true;
          }
          return false;
        default: 
          return false;
      }
    }

    private resolveRuleKeys(
      lifeEventId: string,
      effectiveDateRuleId: string,
      isFutureEvent: boolean,
      isLifeEventDateSameAsCurrentDate: boolean,
      isFirstOfTheMonth: boolean,
      config: SepEffectiveDateConfig,
      firstOfNextMonthAfterEventDate: string []
    ): string[] | null {
      
      let ruleKeys: string[] | null = null;
      if (lifeEventId) {
        ruleKeys = this.extractRuleKeys(config, isFutureEvent, isLifeEventDateSameAsCurrentDate, isFirstOfTheMonth, firstOfNextMonthAfterEventDate);
      }

      if (ruleKeys === null) {
        return [];
      }

      // REJECT check
      if (ruleKeys.length === 1 && ruleKeys[0] === 'REJECT') {
        return null;
      }

      return ruleKeys;
  }

  private extractRuleKeys(
      rule: SepEffectiveDateConfig,
      isFutureEvent: boolean,
      isLifeEventDateSameAsCurrentDate: boolean,
      isFirstOfTheMonth: boolean,
      firstOfNextMonthAfterEventDate: string[]
    ): string[] {
      
      const baseKeys: string[] = isLifeEventDateSameAsCurrentDate ? firstOfNextMonthAfterEventDate : isFutureEvent ? rule.futureEvent : rule.pastEvent;

      if ( environment.initData.brand.ASPID === "MN" && (isFutureEvent || isLifeEventDateSameAsCurrentDate) && isFirstOfTheMonth && !baseKeys.includes('DATE_OF_EVENT')) {
        return [...baseKeys, 'DATE_OF_EVENT'];
      }

      return baseKeys;
    }

    /**
   * Converts an array of SepEffectiveDateRuleKey values into Moment dates.
   * Service dates (from the API) are included when DATE_OF_SERVICE_DATES is
   * needed; currently the service dates are appended for NEXT_TWO_MONTHS_FROM_APP_DATE.
   */
  private resolveRuleKeysToDates(
    ruleKeys: String[],
    dateScenarios: any,
    serviceEffectiveDates: Moment[]
  ): Moment[] {
    const results: Moment[] = [];

    for (const key of ruleKeys) {
      switch (key) {
        case 'DATE_OF_EVENT':
          results.push(this.date.getDateAsMoment(dateScenarios.eventRelative.dateOf));
          break;
        case 'FIRST_OF_NEXT_MONTH_AFTER_EVENT_DATE':
          results.push(this.date.getDateAsMoment(dateScenarios.eventRelative.nextMonth.firstDay));
          break;
        case 'FIRST_OF_NEXT_MONTH_AFTER_APP_DATE':
          results.push(this.date.getDateAsMoment(dateScenarios.todayRelative.nextMonth.firstDay));
          break;
        case 'FIRST_OF_APP_MONTH':
          results.push(this.date.getDateAsMoment(dateScenarios.todayRelative.firstOfCurrentMonth));
          break;
        case 'NEXT_TWO_MONTHS_FROM_APP_DATE':
          // Use the service-provided effective dates (which already represent the
          // next two months from the application date)
          for (const effDate of serviceEffectiveDates) {
            results.push(effDate);
          }
          break;
        case 'REJECT':
          break;
        default:
          console.warn(`getSepEffectiveDates: unknown rule key "${key}"`);
      }
    }

    return results;
  }
}
